import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LogoLink = () => (
  <Link
    to="/"
    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#1E2A5E] font-body"
    data-testid="legal-back-link"
  >
    <ArrowLeft className="w-4 h-4" /> Back to Knight Notary Services
  </Link>
);

const LegalShell = ({ title, updated, children, testId }) => (
  <div className="min-h-screen bg-white text-[#0F172A]" data-testid={testId}>
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <LogoLink />
      <h1 className="mt-6 font-display text-4xl lg:text-5xl font-light tracking-tighter text-[#0F172A]">
        {title}
      </h1>
      <p className="mt-3 text-sm text-slate-500 font-body">Last updated: {updated}</p>
      <div className="mt-10 space-y-6 text-slate-700 font-body leading-relaxed text-[15.5px]">
        {children}
      </div>
      <div className="mt-16 pt-8 border-t border-slate-200">
        <LogoLink />
      </div>
    </div>
  </div>
);

const H = ({ children }) => (
  <h2 className="font-display text-2xl font-medium text-[#0F172A] tracking-tight mt-10">
    {children}
  </h2>
);

export const PrivacyPolicy = () => (
  <LegalShell title="Privacy Policy" updated="June 2026" testId="page-privacy">
    <p>
      Knight Notary Services ("we", "us", "our") respects your privacy. This
      Privacy Policy explains what information we collect, how we use it, and
      the choices you have when you visit
      {" "}<a className="text-[#1E2A5E] underline" href="https://knight-notary.com">knight-notary.com</a>{" "}
      or engage our notary services.
    </p>

    <H>Information We Collect</H>
    <p>
      When you contact us through our website form, request an appointment, or
      call/email us, we may collect: your name, phone number, email address,
      company (if provided), the type of notarial service requested, and any
      details you include in your message. Notary journals are maintained
      separately in accordance with applicable state law.
    </p>

    <H>How We Use Your Information</H>
    <ul className="list-disc pl-6 space-y-2">
      <li>To respond to your inquiry and schedule notarial services.</li>
      <li>To complete the requested notarization and any related recordkeeping.</li>
      <li>To send appointment confirmations, follow-ups, or receipts.</li>
      <li>To comply with legal, tax, and notarial recordkeeping obligations.</li>
    </ul>

    <H>How We Share Information</H>
    <p>
      We do not sell your personal information. We may share limited information
      with trusted service providers who help us operate the business (for
      example: our CRM, appointment scheduler, or email provider) and only to
      the extent necessary to deliver the service. We may also disclose
      information if required by law or valid legal process.
    </p>

    <H>Data Retention</H>
    <p>
      Contact form submissions and appointment records are retained for as long
      as necessary to provide services and to comply with legal, tax, and
      notarial recordkeeping requirements.
    </p>

    <H>Your Choices</H>
    <p>
      You may request access to, correction of, or deletion of your personal
      information by emailing
      {" "}<a className="text-[#1E2A5E] underline" href="mailto:damon.knight@knight-notary.com">damon.knight@knight-notary.com</a>.{" "}
      Note that we may need to retain certain notarial records as required by
      state law even after a deletion request.
    </p>

    <H>Cookies & Analytics</H>
    <p>
      Our website may use cookies or similar technologies to remember
      preferences and measure how visitors interact with the site. You can
      control cookies through your browser settings.
    </p>

    <H>Third-Party Links</H>
    <p>
      Our site links to third-party services (e.g., our scheduling and CRM
      providers). Their privacy practices are governed by their own policies.
    </p>

    <H>Contact</H>
    <p>
      Questions about this policy? Contact Damon Knight at{" "}
      <a className="text-[#1E2A5E] underline" href="mailto:damon.knight@knight-notary.com">
        damon.knight@knight-notary.com
      </a>{" "}
      or call{" "}
      <a className="text-[#1E2A5E] underline" href="tel:+15132966926">
        1 (513) 296-6926
      </a>
      .
    </p>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell title="Terms and Conditions" updated="June 2026" testId="page-terms">
    <p>
      These Terms and Conditions ("Terms") govern your use of the Knight Notary
      Services website and the notarial services we provide. By using our
      website or engaging our services, you agree to these Terms.
    </p>

    <H>Services</H>
    <p>
      Knight Notary Services provides mobile and in-office notarial services
      including, but not limited to: acknowledgments, jurats, loan signings,
      real estate closings, apostille facilitation, power of attorney, and
      general notary work. All notarial acts are performed in accordance with
      the laws of the state in which the notary is commissioned.
    </p>

    <H>Appointments & Fees</H>
    <ul className="list-disc pl-6 space-y-2">
      <li>
        Appointments may be booked via our website, phone, email, or scheduling
        link. Booking is confirmed only when you receive confirmation from us.
      </li>
      <li>
        Fees are quoted based on service type, document volume, travel, and
        time of day. After 7:00 PM local time, a surcharge fee applies.
      </li>
      <li>
        Cancellations made less than 2 hours before the scheduled appointment
        may incur a travel/booking fee.
      </li>
    </ul>

    <H>Signer Responsibilities</H>
    <ul className="list-disc pl-6 space-y-2">
      <li>Signers must present valid, current government-issued photo ID.</li>
      <li>Signers must appear personally, be willing, and be aware of the contents of the document.</li>
      <li>All required signers must be present at the time of notarization.</li>
    </ul>

    <H>What We Cannot Do</H>
    <p>
      Notaries public are not attorneys. We do not provide legal advice, prepare
      legal documents, select forms for your situation, or explain the legal
      effect of any document. If you need legal advice, please consult a
      licensed attorney.
    </p>

    <H>Confidentiality</H>
    <p>
      We treat the details of every appointment with strict confidentiality.
      Notary journal entries are maintained as required by law and may be
      produced only in response to lawful process.
    </p>

    <H>Website Use</H>
    <p>
      All content on this website is provided for informational purposes only.
      You may not copy, reproduce, or distribute content without written
      permission. We may update the site, prices, or these Terms at any time.
    </p>

    <H>Limitation of Liability</H>
    <p>
      To the maximum extent permitted by law, Knight Notary Services' total
      liability for any claim arising out of or relating to our services shall
      not exceed the amount actually paid by you for the specific service
      giving rise to the claim.
    </p>

    <H>Governing Law</H>
    <p>
      These Terms are governed by the laws of the State of Ohio, without regard
      to conflict-of-law principles. Any dispute shall be brought exclusively
      in the state or federal courts located in Hamilton County, Ohio.
    </p>

    <H>Contact</H>
    <p>
      Damon Knight, Knight Notary Services. Email:{" "}
      <a className="text-[#1E2A5E] underline" href="mailto:damon.knight@knight-notary.com">
        damon.knight@knight-notary.com
      </a>
      . Phone:{" "}
      <a className="text-[#1E2A5E] underline" href="tel:+15132966926">
        1 (513) 296-6926
      </a>
      . Fax: 1 (513) 399-4989.
    </p>
  </LegalShell>
);
